/**
 * CountryFieldType — kraj (ISO 3166-1 alpha-2). Storage: PG CHAR(2).
 *
 * MVP: hardcoded lista 14 krajów którymi seedujemy `countries` w PUB. Po MVP: fetch z `/api/countries`.
 */

import { useEffect, useRef } from 'react';

import { Dropdown } from '@/components/Dropdown';

import type { FieldInputProps, FieldTypeDefinition } from '../FieldRegistry';

// Skrócona lista — 14 krajów ISO seedowanych w `seed-pub.ts countries`
const SHORT_LIST: Array<{ code: string; name: string }> = [
  { code: 'PL', name: 'Polska' },
  { code: 'DE', name: 'Niemcy' },
  { code: 'CZ', name: 'Czechy' },
  { code: 'SK', name: 'Słowacja' },
  { code: 'GB', name: 'Wielka Brytania' },
  { code: 'US', name: 'USA' },
  { code: 'FR', name: 'Francja' },
  { code: 'IT', name: 'Włochy' },
  { code: 'ES', name: 'Hiszpania' },
  { code: 'NL', name: 'Holandia' },
  { code: 'BE', name: 'Belgia' },
  { code: 'AT', name: 'Austria' },
  { code: 'UA', name: 'Ukraina' },
  { code: 'NO', name: 'Norwegia' },
];

// Uwaga (batch Dropdown): `Dropdown` nie przyjmuje `aria-invalid` — sygnalizacja błędu
// walidacji zostaje po stronie ramki callsite'a (tekst błędu); `props.error` nieużywane.
function CountryInput({ field, value, onChange, disabled, autoFocus }: FieldInputProps) {
  const stringValue = typeof value === 'string' ? value : '';

  // `Dropdown` nie wspiera natywnego `autoFocus` — fokusujemy trigger przez ref.
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (autoFocus) triggerRef.current?.focus();
  }, [autoFocus]);

  return (
    <Dropdown
      ref={triggerRef}
      options={[
        { value: '', label: '— wybierz kraj —' },
        ...SHORT_LIST.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` })),
      ]}
      value={stringValue}
      onChange={(v) => onChange(v || null)}
      disabled={disabled}
      data-testid={`field-${field.slug}-input`}
    />
  );
}

export const CountryFieldType: FieldTypeDefinition = {
  type: 'country',
  renderInput: (props) => <CountryInput {...props} />,
  renderCell: (_field, value) => {
    if (typeof value !== 'string' || !value) return '';
    const c = SHORT_LIST.find((x) => x.code === value);
    return c ? c.name : value;
  },
  defaultValue: null,
};
